import React from 'react';
import { View, Text, StyleSheet, TouchableOpacity } from 'react-native';
import { Feather } from '@expo/vector-icons';
import { useTranslation } from 'react-i18next';
import { Paprika, PaprikaSpacing, PaprikaTypography, PaprikaRadius } from '../../styles/theme';

interface SubOptionProps {
  name: string;
  ratio?: string;
  notes?: string;
  veg?: boolean;
  selected?: boolean;
  /** This substitute is in the user's pantry right now. */
  owned?: boolean;
  onUse: () => void;
}

export const SubOption: React.FC<SubOptionProps> = ({
  name,
  ratio,
  notes,
  veg,
  selected,
  owned,
  onUse,
}) => {
  const { t } = useTranslation();
  const noteLine = [ratio, notes].filter(Boolean).join(' · ');

  return (
    <View style={[styles.card, owned && styles.cardOwned, selected && styles.cardSelected]}>
      <View style={styles.textBlock}>
        {owned && (
          <View style={styles.ownedBadge}>
            <Feather name="check" size={9} color={Paprika.success} />
            <Text style={styles.ownedBadgeText}>
              {t('recipeDetail.inYourPantry', 'Dans votre garde-manger')}
            </Text>
          </View>
        )}
        <View style={styles.nameRow}>
          <Text style={styles.name}>{name}</Text>
          {veg && (
            <View style={styles.vegChip}>
              <Text style={styles.vegChipText}>{t('recipeDetail.veg', 'végé')}</Text>
            </View>
          )}
        </View>
        {noteLine ? <Text style={styles.note}>{noteLine}</Text> : null}
      </View>

      <TouchableOpacity
        style={[styles.useButton, selected && styles.useButtonSelected]}
        onPress={onUse}
        activeOpacity={0.8}
      >
        <Text style={[styles.useButtonText, selected && styles.useButtonTextSelected]}>
          {selected ? t('recipeDetail.used', 'Utilisé') : t('recipeDetail.use', 'Utiliser')}
        </Text>
      </TouchableOpacity>
    </View>
  );
};

const styles = StyleSheet.create({
  card: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: PaprikaSpacing.sm,
    backgroundColor: Paprika.cardBg,
    borderRadius: PaprikaRadius.md,
    borderWidth: 1,
    borderColor: Paprika.border,
    padding: PaprikaSpacing.md,
  },
  cardSelected: {
    borderColor: Paprika.accent,
    backgroundColor: Paprika.brandTint,
  },
  // Sage, the colour this app already uses for "in stock". Tonal shift, no
  // heavier border — the tint alone lifts the row out of the list.
  cardOwned: {
    borderColor: Paprika.successSoft,
    backgroundColor: Paprika.successSoft,
  },
  ownedBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    alignSelf: 'flex-start',
    gap: 4,
    backgroundColor: Paprika.cardBg,
    borderRadius: PaprikaRadius.full,
    paddingVertical: 3,
    paddingHorizontal: 8,
    marginBottom: 2,
  },
  ownedBadgeText: {
    fontFamily: 'Inter-SemiBold',
    fontWeight: '600',
    fontSize: 9.5,
    letterSpacing: 0.4,
    textTransform: 'uppercase',
    color: Paprika.success,
  },
  textBlock: {
    flexShrink: 1,
    gap: 2,
  },
  nameRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: PaprikaSpacing.xs,
    flexWrap: 'wrap',
  },
  name: {
    ...PaprikaTypography.titleMedium,
    color: Paprika.textPrimary,
  },
  vegChip: {
    backgroundColor: `${Paprika.success}1F`,
    borderRadius: PaprikaRadius.full,
    paddingVertical: 1,
    paddingHorizontal: 7,
  },
  vegChipText: {
    ...PaprikaTypography.labelSmall,
    color: '#5d7c43',
    letterSpacing: 0.3,
  },
  note: {
    ...PaprikaTypography.caption,
    color: Paprika.textSecondary,
  },
  useButton: {
    paddingVertical: 7,
    paddingHorizontal: PaprikaSpacing.md,
    borderRadius: PaprikaRadius.full,
    borderWidth: 1,
    borderColor: Paprika.accent,
    backgroundColor: 'transparent',
  },
  useButtonSelected: {
    backgroundColor: Paprika.accent,
  },
  useButtonText: {
    ...PaprikaTypography.labelMedium,
    color: Paprika.accent,
  },
  useButtonTextSelected: {
    color: '#fff',
  },
});

export default SubOption;
